import React, { useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { ClubConditionsData } from '../../types/clubConditions';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartColors, getChartBackgroundColor, getChartJsOptions } from '../../utils/themeHelpers';
import { LoadingSpinner } from '../ui';
import ChartControls from '../ui/ChartControls';
import { CHART_GUIDES } from '../../constants/chartGuides';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useClubConditionsDataForCustomDashboard } from '../../hooks/useClubConditionsDataForCustomDashboard';
import ErrorMessage from '../ui/ErrorMessage';
import { calculateLinearRegressionLine } from '../../utils/chartRegression';
import { percentageYScale } from './clubConditionsChartUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

interface UsedCreditRatioChartProps {
  data?: ClubConditionsData[]; // Optional when used in custom dashboard
  isLoading?: boolean;
  error?: any;
  branchId?: number;
  branchName?: string;
  timeRange?: any;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
  // Custom dashboard specific props
  useCustomDashboardData?: boolean; // Flag to indicate if we should fetch data ourselves
}

const UsedCreditRatioChart: React.FC<UsedCreditRatioChartProps> = ({ 
  data = [],
  isLoading,
  error,
  branchId,
  branchName,
  timeRange,
  onError,
  showLoading = true,
  compact = false,
  useCustomDashboardData = false // Default to false to maintain backward compatibility
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const chartColors = getChartColors(currentTheme);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // For custom dashboard usage, fetch data ourselves
  const { 
    data: fetchedData, 
    isLoading: isFetching, 
    error: fetchError 
  } = useClubConditionsDataForCustomDashboard(
    useCustomDashboardData ? branchId : undefined, 
    useCustomDashboardData ? timeRange : undefined
  );
  
  // Component metadata
  const componentId = 'used-credit-ratio-chart';
  const componentTitle = 'میزان اعتبار مصرف شده به فروش';

  // Use fetched data when in custom dashboard mode, otherwise use passed data
  const effectiveData = useCustomDashboardData ? (fetchedData?.data || []) : data;
  const effectiveIsLoading = useCustomDashboardData ? isFetching : isLoading;
  const effectiveError = useCustomDashboardData ? fetchError : error;
  
  const formatMonthYear = (item: ClubConditionsData) => {
    return `${item.month}`;
  };

  // Sort data by year and monthNum in ascending order
  const sortedData = [...effectiveData].sort((a, b) => {
    if (a.year !== b.year) {
      return a.year - b.year;
    }
    return a.monthNum - b.monthNum;
  });

  // Use UsedToAllSales from backend (multiply by 100 for percentage)
  const chartData = sortedData.map(item => ({
    month: formatMonthYear(item),
    ratio: (item.usedToAllSales || 0) * 100
  }));
  const trendlineData = calculateLinearRegressionLine(chartData.map(item => item.ratio));
  const yValues = [
    ...chartData.map((item) => item.ratio),
    ...trendlineData.filter((value): value is number => value !== null),
  ];

  // Chart options
  const barChartOptions = {
    ...getChartJsOptions(currentTheme),
    scales: {
      ...getChartJsOptions(currentTheme).scales,
      x: {
        ...getChartJsOptions(currentTheme).scales.x,
        ticks: {
          ...getChartJsOptions(currentTheme).scales?.x?.ticks,
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: percentageYScale(yValues, getChartJsOptions(currentTheme).scales.y as Record<string, unknown>),
    },
    plugins: {
      ...getChartJsOptions(currentTheme).plugins,
      tooltip: {
        ...getChartJsOptions(currentTheme).plugins?.tooltip,
        callbacks: {
          label: function(context: any) {
            return `${context.parsed.y.toFixed(2)}%`;
          }
        }
      }
    }
  };
  
  // Handle loading state
  if (showLoading && effectiveIsLoading) {
    return (
      <div className="card-base card-padding">
        <div className="flex items-center justify-center min-h-[350px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری نمودار…"
          />
        </div>
      </div>
    );
  }
  
  // Handle error
  if (effectiveError) {
    if (onError && effectiveError instanceof Error) {
      onError(effectiveError);
    }
    if (useCustomDashboardData) {
      return (
        <div className="card-base card-padding">
          <div className="flex items-center justify-center min-h-[350px]">
            <ErrorMessage message="خطا در بارگذاری داده‌های نمودار" />
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="chart"
          dashboardSource="club-conditions"
          onInfo={() => openGuide(CHART_GUIDES.usedCreditRatioChart)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'club-conditions',
            componentType: 'chart',
            description: 'نسبت اعتبار مصرف شده به فروش کل در بازه زمانی',
            props: { branchId, timeRange, compact, useCustomDashboardData: true },
            gridLayout: {
              containerClass: 'grid grid-cols-1 gap-6'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
          className="mb-4"
        />
        <h3 className="title-card">میزان اعتبار مصرف شده به فروش</h3>
        <p className="text-text-secondary text-sm mt-1 text-center">نسبت اعتبار مصرف شده به فروش کل در بازه زمانی</p>
      </div>
      
      <div style={{ height: compact ? 300 : 350, width: '100%' }}>
        {chartData.length > 0 ? (
          <Chart
            type='bar'
            data={{
              labels: chartData.map(item => item.month),
              datasets: [
                {
                  type: 'bar' as const,
                  label: branchName || 'شعبه',
                  data: chartData.map(item => item.ratio),
                  order: 2,
                  backgroundColor: getChartBackgroundColor(0, currentTheme, 0.8),
                  borderColor: chartColors[0],
                  borderWidth: 2,
                  borderRadius: 6,
                },
                {
                  type: 'line' as const,
                  label: 'خط روند',
                  data: trendlineData,
                  order: 1,
                  borderColor: '#dc2626',
                  backgroundColor: 'rgba(220, 38, 38, 0.2)',
                  borderWidth: 3,
                  pointRadius: 3,
                  pointHoverRadius: 5,
                  pointBackgroundColor: '#dc2626',
                  pointBorderColor: '#ffffff',
                  pointBorderWidth: 1.5,
                  tension: 0,
                  fill: false,
                }
              ]
            }}
            options={barChartOptions}
            aria-label="نمودار ستونی میزان اعتبار مصرف شده به فروش"
          />
        ) : (
          <div className="flex items-center justify-center p-8 text-text-tertiary text-sm">
            <p>داده‌ای برای نمایش نمودار وجود ندارد</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsedCreditRatioChart;
